import React from "react";
import Link from "next/link";
import { Pagination, PaginationItem, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import OopsPage from "@pageContent/OopsPage";
import {
  InfiniteCollectionContainer,
  ItemsContainer,
  Title,
  TitleWrapper,
} from "@pageContent/common/InfiniteCollection/InfiniteCollection";
import InfiniteCollectionItem from "@pageContent/common/InfiniteCollection/InfiniteCollectionItem";
import { CollectionSkeleton } from "@pageContent/common/Skeletons/InfiniteCollectionSkeleton";
import { colors, fonts } from "@constants/cssVariables";
import { fetchByEndpoint } from "@utils/pageContent/fetchData";

const noResultText = "Helaas kunnen we geen items vinden.";

const Items = styled(ItemsContainer)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    h5: {
      gridColumn: "1 / -1",
      margin: theme.spacing(-2, 0),
    },
  },
}));

const NoItemsText = styled("p")({
  font: fonts.bodyLarge,
  color: colors.neutral80,
  alignSelf: "start",
});

type CollectionResponse = LinkList & { total: number };

const PaginatedOverview: React.FC<{
  category?: Pick<CategoryEntry, "articleEndpoint" | "videoEndpoint">;
  page: number;
  limit: number;
  collectionTitle: string;
  currentPath?: string;
  onPageChange?: (page: number) => void;
  isVideoOverview: boolean;
  activeId?: string;
  initialData?: CollectionResponse;
}> = ({
  page,
  category,
  limit,
  collectionTitle,
  currentPath,
  isVideoOverview,
  initialData,
  activeId,
  onPageChange,
}) => {
  const { articleEndpoint, videoEndpoint } = category || {};
  const skip = (page - 1) * limit;

  const { data, isLoading, error } = useQuery({
    queryKey: ["category-overview", isVideoOverview, videoEndpoint, articleEndpoint, skip, limit],
    queryFn: () =>
      fetchByEndpoint<CollectionResponse>(
        `${isVideoOverview ? videoEndpoint : articleEndpoint}?skip=${skip}&limit=${limit}`
      ),
    enabled: !!category,
    initialData,
  });

  const { items, total } = data || {};
  const count = total ? Math.ceil(total / limit) : 0;

  if (isLoading || !category)
    return (
      <InfiniteCollectionContainer>
        <TitleWrapper>
          <Title>{collectionTitle}</Title>
        </TitleWrapper>

        <ItemsContainer>
          <CollectionSkeleton title={collectionTitle} limit={limit} />
        </ItemsContainer>
      </InfiniteCollectionContainer>
    );

  if (!items) return <OopsPage error={error} />;

  return (
    <>
      <InfiniteCollectionContainer>
        <TitleWrapper>
          <Title>{collectionTitle}</Title>
        </TitleWrapper>

        {items.length ? (
          <Items>
            {items.map((item, index) => (
              <InfiniteCollectionItem
                key={item.id}
                position={index + 1}
                sectionTitle={collectionTitle}
                isActive={!!item.videoGuid && item.videoGuid === activeId}
                link={{ ...item, hasVideo: true }}
              />
            ))}
          </Items>
        ) : (
          <NoItemsText>{noResultText}</NoItemsText>
        )}
      </InfiniteCollectionContainer>
      {!!count && (
        <Pagination
          sx={{
            marginBottom: 10,
            "& > .MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
          page={page}
          count={count}
          showFirstButton
          showLastButton
          onChange={(_event, page) => onPageChange && page && onPageChange(page)}
          renderItem={(item) => (
            <PaginationItem
              {...(!onPageChange && {
                component: Link,
                href: `${item.page === 1 ? currentPath : `${currentPath}?page=${item.page}`}`,
              })}
              {...item}
            />
          )}
        />
      )}
    </>
  );
};
export default PaginatedOverview;
